import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from '../i18n/useTranslation';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Calendar = ({ onDateSelect, selectedDate }) => {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  // Staff can browse the full history; patients and guests get one month back.
  const isStaff = ['admin', 'super'].includes(user?.role);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const earliestMonth = (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  })();

  const canGoBack = isStaff || new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() - 1,
    1
  ) >= earliestMonth;
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchSlotsForMonth();
  }, [currentMonth]);

  // Refresh when component remounts (via key prop from parent)
  useEffect(() => {
    fetchSlotsForMonth();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Scroll to selected date in mobile carousel
    if (isMobile && selectedDate && scrollContainerRef.current) {
      const selectedDateObj = new Date(selectedDate + 'T00:00:00');
      const today = new Date();
      const daysDiff = Math.floor((selectedDateObj - today) / (1000 * 60 * 60 * 24));
      const scrollIndex = Math.max(0, daysDiff);
      const scrollElement = scrollContainerRef.current.children[scrollIndex];
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedDate, isMobile]);

  const fetchSlotsForMonth = async () => {
    setLoading(true);
    try {
      // Determine date range based on current month view
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      // Start from beginning of current month
      const startDate = new Date(year, month, 1);
      startDate.setHours(0, 0, 0, 0);
      
      // End at end of current month + 2 months ahead (to cover carousel and future months)
      const endDate = new Date(year, month + 3, 0);
      endDate.setHours(23, 59, 59, 999);
      
      const response = await axios.get(`${API_URL}/bookings/slots/all`, {
        params: {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }
      });
      console.log(`Calendar: Fetched ${response.data?.length || 0} slots for month ${month + 1}/${year}`);
      setSlots(response.data || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getSlotsForDate = (date) => {
    if (!date || !slots || slots.length === 0) return { available: 0, booked: 0, total: 0 };
    
    // Normalize the input date to start of day for comparison
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const checkTime = checkDate.getTime();
    
    const dateSlots = slots.filter(slot => {
      if (!slot || !slot.date) return false;
      
      // Normalize slot date to start of day for comparison
      const slotDate = new Date(slot.date);
      slotDate.setHours(0, 0, 0, 0);
      const slotTime = slotDate.getTime();
      
      // Compare timestamps to avoid timezone issues
      return slotTime === checkTime;
    });
    
    return {
      available: dateSlots.filter(s => s && s.isAvailable === true).length,
      booked: dateSlots.filter(s => s && s.isAvailable === false).length,
      total: dateSlots.length
    };
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    // Normalize dates to avoid timezone issues
    const selected = new Date(selectedDate + 'T00:00:00');
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    return (
      date.getDate() === selected.getDate() &&
      date.getMonth() === selected.getMonth() &&
      date.getFullYear() === selected.getFullYear()
    );
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const handleDateClick = (date) => {
    if (!date || isPast(date)) return;
    // Format date as YYYY-MM-DD without timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    onDateSelect(`${year}-${month}-${day}`);
  };

  const goToPreviousMonth = () => {
    if (!canGoBack) return;
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    const today = new Date().toISOString().split('T')[0];
    onDateSelect(today);
    if (isMobile && scrollContainerRef.current) {
      setTimeout(() => {
        const todayElement = scrollContainerRef.current?.querySelector('[data-date="' + today + '"]');
        if (todayElement) {
          todayElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 100);
    }
  };

  const scrollDays = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 80; // Width of one day card + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'next' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getDaysForCarousel = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Show 60 days from today
    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }
    
    return days;
  };

  const monthNames = language === 'ar' 
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const dayNames = language === 'ar'
    ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth();
  const carouselDays = getDaysForCarousel();
  const isRTL = language === 'ar';

  // Mobile Carousel View
  if (isMobile) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-3 overflow-hidden mb-4" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className={`flex items-center justify-between mb-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            onClick={goToPreviousMonth}
            disabled={!canGoBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Previous month"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M15 19l-7-7 7-7"} />
            </svg>
          </button>
          <div className="text-center flex-1 min-w-0 px-2">
            <h2 className="text-base font-bold text-gray-800">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <button
              onClick={goToToday}
              className="text-xs text-blue-600 hover:text-blue-800 mt-1"
            >
              {t('calendar.goToToday')}
            </button>
          </div>
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
            aria-label="Next month"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M9 5l7 7-7 7"} />
            </svg>
          </button>
        </div>

        {/* Day Carousel with Navigation */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scrollDays('prev')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition ${isRTL ? 'left-auto right-0' : ''}`}
            aria-label="Scroll previous"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
            </svg>
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scrollDays('next')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition ${isRTL ? 'right-auto left-0' : ''}`}
            aria-label="Scroll next"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
            </svg>
          </button>

          {/* Scrollable Days Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide pb-3 px-12 mt-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {carouselDays.map((date) => {
              const slotInfo = getSlotsForDate(date);
              const past = isPast(date);
              const today = isToday(date);
              const selected = isSelected(date);
              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              
              const dayName = dayNames[date.getDay()];
              const dayNumber = date.getDate();

              return (
                <button
                  key={dateStr}
                  data-date={dateStr}
                  onClick={() => handleDateClick(date)}
                  disabled={past}
                  className={`
                    flex-shrink-0 w-[72px] h-24 sm:w-20 sm:h-28 rounded-lg border-2 transition-all
                    flex flex-col items-center justify-center p-2.5 sm:p-3
                    ${past 
                      ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                      : selected
                      ? 'bg-blue-600 border-blue-700 text-white shadow-lg scale-105'
                      : today
                      ? 'bg-blue-50 border-blue-300 text-blue-700 font-semibold'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                    }
                  `}
                >
                  <div className={`text-xs sm:text-sm font-medium mb-1.5 ${selected ? 'text-white' : 'text-gray-500'}`}>
                    {dayName}
                  </div>
                  <div className={`text-xl sm:text-2xl font-bold mb-1.5 ${selected ? 'text-white' : ''}`}>
                    {dayNumber}
                  </div>
                  {!past && (
                    <div className="flex items-center justify-center gap-1 w-full mt-1.5">
                      {slotInfo.available > 0 && (
                        <div 
                          className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${selected ? 'bg-green-400' : 'bg-green-500'}`}
                          title={`${slotInfo.available} ${t('calendar.available')}`}
                        ></div>
                      )}
                      {slotInfo.booked > 0 && (
                        <div 
                          className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${selected ? 'bg-red-400' : 'bg-red-500'}`}
                          title={`${slotInfo.booked} ${t('calendar.booked')}`}
                        ></div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className={`mt-3 flex items-center justify-center gap-3 text-xs text-gray-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
            <span>{t('calendar.available')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
            <span>{t('calendar.booked')}</span>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Full Calendar View
  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-2 sm:p-3 md:p-4 lg:p-6 overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`flex items-center justify-between mb-3 sm:mb-4 md:mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <button
          onClick={goToPreviousMonth}
          disabled={!canGoBack}
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          aria-label="Previous month"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M15 19l-7-7 7-7"} />
          </svg>
        </button>
        <div className="text-center flex-1 min-w-0 px-2">
          <h2 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-800 truncate">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <button
            onClick={goToToday}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 mt-0.5 sm:mt-1"
          >
            {t('calendar.goToToday')}
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
          aria-label="Next month"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M9 5l7 7-7 7"} />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 md:gap-2 mb-1 sm:mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-[10px] xs:text-xs sm:text-sm font-semibold text-gray-600 py-1 sm:py-1.5 md:py-2 truncate">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 md:gap-2">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square min-h-[32px] sm:min-h-[40px]" />;
          }

          const slotInfo = getSlotsForDate(date);
          const past = isPast(date);
          const today = isToday(date);
          const selected = isSelected(date);

          return (
            <button
              key={date.toISOString()}
              onClick={() => handleDateClick(date)}
              disabled={past}
              className={`
                aspect-square min-h-[32px] sm:min-h-[40px] md:min-h-[48px] p-0.5 sm:p-1 md:p-1.5 lg:p-2 
                rounded sm:rounded-md md:rounded-lg border transition-all 
                text-[10px] xs:text-xs sm:text-sm md:text-base
                flex flex-col items-center justify-center
                ${past 
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                  : selected
                  ? 'bg-blue-600 border-blue-700 text-white shadow-lg scale-105 z-10'
                  : today
                  ? 'bg-blue-50 border-blue-300 text-blue-700 font-semibold hover:bg-blue-100'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                }
              `}
            >
              <div className={`font-medium ${selected ? 'text-white' : ''} leading-tight`}>
                {date.getDate()}
              </div>
              {!past && (
                <div className="flex items-center justify-center gap-0.5 mt-0.5 sm:mt-1 w-full min-h-[2px] sm:min-h-[3px] md:min-h-[4px]">
                  {/* Visual indicators like Outlook */}
                  {slotInfo.available > 0 && (
                    <div 
                      className={`h-[2px] sm:h-1 md:h-1.5 rounded-full ${selected ? 'bg-green-400' : 'bg-green-500'}`} 
                      style={{ width: `${Math.max(20, (slotInfo.available / Math.max(slotInfo.total, 1)) * 100)}%` }}
                      title={`${slotInfo.available} ${t('calendar.available')}`}
                    ></div>
                  )}
                  {slotInfo.booked > 0 && (
                    <div 
                      className={`h-[2px] sm:h-1 md:h-1.5 rounded-full ${selected ? 'bg-red-400' : 'bg-red-500'}`} 
                      style={{ width: `${Math.max(20, (slotInfo.booked / Math.max(slotInfo.total, 1)) * 100)}%` }}
                      title={`${slotInfo.booked} ${t('calendar.booked')}`}
                    ></div>
                  )}
                  {slotInfo.total === 0 && (
                    <div className="h-[2px] sm:h-1 md:h-1.5 w-full"></div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className={`mt-3 sm:mt-4 flex items-center justify-center gap-2 sm:gap-3 md:gap-4 text-[10px] xs:text-xs sm:text-sm text-gray-600 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-100 border border-green-300 rounded flex-shrink-0"></div>
          <span className="whitespace-nowrap">{t('calendar.available')}</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-100 border border-red-300 rounded flex-shrink-0"></div>
          <span className="whitespace-nowrap">{t('calendar.booked')}</span>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
